export const legalDocumentStyles = `
@font-face {
  font-family: "GTF Good Mono";
  src: url("/fonts/GoodMono/GTFGoodMonoTRIAL-Thin.otf") format("opentype");
  font-weight: 100;
  font-display: swap;
}

.zmk-legal-root {
  --zmk-legal-bg: var(--zmk-palette-bg);
  --zmk-legal-bg-deep: var(--zmk-palette-footer);
  --zmk-legal-panel: var(--zmk-product-card-bg);
  --zmk-legal-panel-soft: var(--zmk-legal-panel-soft-base);
  --zmk-legal-ink: var(--zmk-palette-ink);
  --zmk-legal-copy: color-mix(in srgb, var(--zmk-legal-ink) 68%, transparent);
  --zmk-legal-muted: color-mix(in srgb, var(--zmk-legal-ink) 50%, transparent);
  --zmk-legal-faint: color-mix(in srgb, var(--zmk-legal-ink) 38%, transparent);
  --zmk-legal-line: color-mix(in srgb, var(--zmk-legal-ink) 12%, transparent);
  --zmk-legal-line-soft: color-mix(in srgb, var(--zmk-legal-ink) 7%, transparent);
  --zmk-legal-blue: var(--zmk-palette-blue);
  --zmk-legal-blue-hover: var(--zmk-palette-blue-hover);
  --zmk-legal-gold: var(--zmk-palette-gold);
  --zmk-legal-success: var(--zmk-product-success);
  --zmk-legal-danger: var(--zmk-legal-danger-base);
  --zmk-legal-ease: cubic-bezier(0.16, 1, 0.3, 1);

  background:
    linear-gradient(180deg, color-mix(in srgb, var(--zmk-legal-ink) 2.5%, transparent), transparent 16rem),
    var(--zmk-legal-bg);
  color: var(--zmk-legal-ink);
  color-scheme: dark;
  font-family: var(--font-brand-stack);
  font-weight: 300;
  min-height: 100svh;
  overflow-x: clip;
  -webkit-font-smoothing: antialiased;
}

.zmk-legal-root *,
.zmk-legal-root *::before,
.zmk-legal-root *::after {
  box-sizing: border-box;
}

.zmk-legal-root a {
  color: inherit;
  text-decoration: none;
}

.zmk-legal-root button,
.zmk-legal-root input,
.zmk-legal-root textarea,
.zmk-legal-root select {
  font-family: var(--font-brand-stack);
}

.zmk-legal-root ::selection {
  background: color-mix(in srgb, var(--zmk-legal-blue) 35%, transparent);
  color: var(--zmk-legal-ink);
}

.zmk-legal-shell {
  margin: 0 auto;
  max-width: 1240px;
  padding: 104px 48px 96px;
}

.zmk-legal-hero {
  border-bottom: 1px solid var(--zmk-legal-line-soft);
  margin-bottom: 26px;
  padding-bottom: 72px;
}

.zmk-legal-kicker,
.zmk-legal-toc__label,
.zmk-legal-section__eyebrow,
.zmk-legal-meta,
.zmk-legal-ledger__number,
.zmk-legal-card__meta,
.zmk-legal-stat__label {
  font-family: "GTF Good Mono", ui-monospace, monospace;
  font-weight: 100;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.zmk-legal-kicker {
  color: var(--zmk-legal-muted);
  font-size: 11px;
  margin: 0 0 22px;
}

.zmk-legal-title {
  color: var(--zmk-legal-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 68px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 0.98;
  margin: 0;
  max-width: 790px;
  text-wrap: balance;
}

.zmk-legal-description {
  color: var(--zmk-legal-copy);
  font-size: 18px;
  line-height: 1.7;
  margin: 32px 0 0;
  max-width: 700px;
}

.zmk-legal-meta-row {
  align-items: stretch;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 34px;
}

.zmk-legal-meta {
  border: 1px solid var(--zmk-legal-line);
  border-radius: 8px;
  color: var(--zmk-legal-faint);
  display: inline-flex;
  font-size: 10px;
  line-height: 1.4;
  padding: 11px 13px 9px;
}

.zmk-legal-layout {
  display: grid;
  gap: 72px;
  grid-template-columns: 220px minmax(0, 1fr);
}

.zmk-legal-toc {
  align-self: start;
  position: sticky;
  top: 96px;
}

.zmk-legal-toc__label {
  color: var(--zmk-legal-faint);
  font-size: 10px;
  margin: 0 0 20px;
}

.zmk-legal-toc__list {
  border-left: 1px solid var(--zmk-legal-line-soft);
  display: grid;
  gap: 4px;
  margin: 0;
  padding: 0 0 0 14px;
}

.zmk-legal-toc__link {
  color: var(--zmk-legal-faint);
  display: block;
  font-size: 13px;
  line-height: 1.4;
  padding: 8px 0;
  transition: color 160ms var(--zmk-legal-ease);
}

.zmk-legal-toc__link:hover,
.zmk-legal-toc__link.is-active {
  color: var(--zmk-legal-ink);
}

.zmk-legal-content {
  min-width: 0;
}

.zmk-legal-section {
  border-top: 1px solid var(--zmk-legal-line-soft);
  padding: 70px 0;
  scroll-margin-top: 92px;
}

.zmk-legal-section:first-child {
  border-top: 0;
  padding-top: 0;
}

.zmk-legal-section__eyebrow {
  color: var(--zmk-legal-faint);
  font-size: 10px;
  margin: 0 0 22px;
}

.zmk-legal-section__title {
  color: var(--zmk-legal-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 42px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 1.08;
  margin: 0;
  max-width: 740px;
  text-wrap: balance;
}

.zmk-legal-section__description {
  color: var(--zmk-legal-copy);
  font-size: 16px;
  line-height: 1.7;
  margin: 20px 0 0;
  max-width: 720px;
}

.zmk-legal-section__body {
  margin-top: 34px;
  max-width: 760px;
}

.zmk-legal-prose {
  color: var(--zmk-legal-copy);
  display: grid;
  font-size: 15px;
  gap: 18px;
  line-height: 1.78;
}

.zmk-legal-prose strong,
.zmk-legal-prose .is-emphasis {
  color: var(--zmk-legal-danger);
  font-weight: 400;
}

.zmk-legal-link {
  color: var(--zmk-legal-blue-hover);
  text-decoration: underline;
  text-decoration-color: color-mix(in srgb, var(--zmk-legal-blue-hover) 40%, transparent);
  text-underline-offset: 4px;
  transition:
    color 160ms var(--zmk-legal-ease),
    text-decoration-color 160ms var(--zmk-legal-ease);
}

.zmk-legal-link:hover {
  color: var(--zmk-legal-ink);
  text-decoration-color: color-mix(in srgb, var(--zmk-legal-ink) 70%, transparent);
}

.zmk-legal-list {
  color: var(--zmk-legal-copy);
  display: grid;
  gap: 13px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.zmk-legal-list li {
  display: grid;
  gap: 12px;
  grid-template-columns: 12px minmax(0, 1fr);
  line-height: 1.68;
}

.zmk-legal-list li::before {
  background: var(--zmk-legal-blue);
  border-radius: 50%;
  content: "";
  height: 5px;
  margin-top: 11px;
  width: 5px;
}

.zmk-legal-grid {
  display: grid;
  gap: 14px;
}

.zmk-legal-grid--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.zmk-legal-grid--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.zmk-legal-card {
  background: var(--zmk-legal-panel);
  border: 1px solid var(--zmk-legal-line);
  border-radius: 8px;
  min-width: 0;
  padding: 28px;
}

.zmk-legal-card__head {
  align-items: start;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  margin-bottom: 22px;
}

.zmk-legal-card__number {
  color: var(--zmk-legal-blue-hover);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 38px;
  line-height: 1;
}

.zmk-legal-card__meta {
  color: var(--zmk-legal-faint);
  font-size: 9px;
  padding-top: 7px;
  text-align: right;
}

.zmk-legal-card__title {
  color: var(--zmk-legal-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 25px;
  font-weight: 400;
  line-height: 1.1;
  margin: 0;
}

.zmk-legal-card__body {
  color: var(--zmk-legal-copy);
  font-size: 14px;
  line-height: 1.72;
  margin-top: 14px;
}

.zmk-legal-ledger {
  border-top: 1px solid var(--zmk-legal-line);
  display: grid;
}

.zmk-legal-ledger__row {
  border-bottom: 1px solid var(--zmk-legal-line-soft);
  display: grid;
  gap: 32px;
  grid-template-columns: 92px minmax(0, 1fr);
  padding: 30px 0;
}

.zmk-legal-ledger__number {
  color: var(--zmk-legal-faint);
  font-size: 10px;
}

.zmk-legal-ledger__title {
  color: var(--zmk-legal-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 25px;
  font-weight: 400;
  line-height: 1.15;
  margin: 0 0 16px;
}

.zmk-legal-ledger__body {
  color: var(--zmk-legal-copy);
  display: grid;
  font-size: 15px;
  gap: 16px;
  line-height: 1.74;
}

.zmk-legal-stat-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 10px;
}

.zmk-legal-stat {
  border-top: 1px solid var(--zmk-legal-line-soft);
  padding-top: 18px;
}

.zmk-legal-stat__value {
  color: var(--zmk-legal-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 30px;
  line-height: 1;
  margin: 0;
}

.zmk-legal-stat__label {
  color: var(--zmk-legal-faint);
  font-size: 9px;
  line-height: 1.5;
  margin: 10px 0 0;
}

.zmk-legal-contact {
  display: grid;
  gap: 22px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.zmk-legal-contact address {
  color: var(--zmk-legal-copy);
  font-style: normal;
  line-height: 1.65;
}

.zmk-legal-contact .zmk-legal-card__meta {
  padding-top: 0;
  text-align: left;
}

.zmk-legal-cta {
  background: var(--zmk-legal-bg-deep);
  border: 1px solid var(--zmk-legal-line-soft);
  border-radius: 8px;
  margin-top: 52px;
  padding: 60px;
  text-align: center;
}

.zmk-legal-cta__title {
  color: var(--zmk-legal-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 46px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 1.05;
  margin: 0 auto;
  max-width: 640px;
  text-wrap: balance;
}

.zmk-legal-cta__body {
  color: var(--zmk-legal-copy);
  font-size: 16px;
  line-height: 1.65;
  margin: 20px auto 0;
  max-width: 620px;
}

.zmk-legal-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 32px;
}

.zmk-legal-button {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  font-size: 15px;
  font-weight: 400;
  gap: 10px;
  justify-content: center;
  min-height: 48px;
  padding: 13px 24px;
  transition:
    background 180ms var(--zmk-legal-ease),
    border-color 180ms var(--zmk-legal-ease),
    color 180ms var(--zmk-legal-ease),
    transform 180ms var(--zmk-legal-ease);
}

.zmk-legal-button:hover {
  transform: translateY(-1px);
}

.zmk-legal-button--primary {
  background: var(--zmk-legal-blue);
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 12%, transparent);
  color: var(--zmk-legal-ink);
}

.zmk-legal-button--primary:hover {
  background: var(--zmk-legal-blue-hover);
}

.zmk-legal-button--secondary {
  background: transparent;
  border: 1px solid var(--zmk-legal-line);
  color: var(--zmk-legal-ink);
}

.zmk-legal-button--secondary:hover {
  border-color: color-mix(in srgb, var(--zmk-legal-ink) 28%, transparent);
}

.zmk-legal-button svg {
  height: 15px;
  width: 15px;
}

@media (max-width: 1023px) {
  .zmk-legal-shell {
    padding: 88px 32px 76px;
  }

  .zmk-legal-title {
    font-size: 52px;
  }

  .zmk-legal-layout {
    display: block;
  }

  .zmk-legal-toc {
    background: color-mix(in srgb, var(--zmk-legal-bg) 92%, transparent);
    border-bottom: 1px solid var(--zmk-legal-line-soft);
    border-top: 1px solid var(--zmk-legal-line-soft);
    margin: 0 -32px 44px;
    overflow-x: auto;
    padding: 14px 32px;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .zmk-legal-toc__label {
    display: none;
  }

  .zmk-legal-toc__list {
    border-left: 0;
    display: flex;
    gap: 22px;
    padding: 0;
    width: max-content;
  }

  .zmk-legal-toc__link {
    font-size: 12px;
    padding: 4px 0;
    white-space: nowrap;
  }

  .zmk-legal-grid--three,
  .zmk-legal-grid--two {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .zmk-legal-shell {
    padding: 72px 20px 64px;
  }

  .zmk-legal-hero {
    padding-bottom: 54px;
  }

  .zmk-legal-title {
    font-size: 38px;
    line-height: 1.04;
  }

  .zmk-legal-description {
    font-size: 16px;
    margin-top: 24px;
  }

  .zmk-legal-toc {
    margin-left: -20px;
    margin-right: -20px;
    padding-left: 20px;
    padding-right: 20px;
  }

  .zmk-legal-section {
    padding: 52px 0;
  }

  .zmk-legal-section__title {
    font-size: 32px;
  }

  .zmk-legal-section__body {
    margin-top: 28px;
  }

  .zmk-legal-card {
    padding: 22px;
  }

  .zmk-legal-ledger__row {
    gap: 14px;
    grid-template-columns: 1fr;
    padding: 26px 0;
  }

  .zmk-legal-stat-grid,
  .zmk-legal-contact {
    grid-template-columns: 1fr;
  }

  .zmk-legal-cta {
    padding: 38px 22px;
  }

  .zmk-legal-cta__title {
    font-size: 34px;
  }

  .zmk-legal-actions {
    align-items: stretch;
    flex-direction: column;
  }
}
`;
